import React,{useContext,useEffect, useState, useRef,useReducer, useMemo} from 'react';
import { DatabaseContext } from '../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { getRequest, postRequest } from '../../api/ApiController';
import { SaveIcon, AddIcon, CopyIcon, PrintIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon} from "../svg/svgIcons";
import { Dialog } from 'primereact/dialog';
import { InfinitySpin } from  'react-loader-spinner';
import AuthContext from '../../components/AuthContext';
import UtilContext from '../../components/UtilContext';
import { LocaleContext } from '../../components/LocaleContext';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { useTrainingCompanies } from '../../utils/useTrainingCompanies';
import {
    TRAINING_TEMPLATE_PREVIEW_PATH,
    TRAINING_TEMPLATE_APPLY_PATH,
    TRAINING_CARD_LEGACY_PATH,
    applyCandidateCascade,
    beginTemplateApply,
    buildTemplateApplyPayload,
    catalogLabel,
    contactLabel,
    departmentNameForCandidate,
    groupTemplateCandidates,
    isCandidateCheckable,
    machineLabels,
    missingConfigurationFieldKeys,
    nextTemplatePreviewSession,
    parseTemplateApplyResponse,
    previewMatchesForm,
    resolveTemplateApplyErrorMessage,
    resolveTemplatePreviewErrorMessage,
    templateApplyResultCounts,
    uniqueActiveTemplateRecords,
    overlayTemplateRowsWithPreview,
} from '../../utils/trainingTemplateApply';
import { emitTrainingCardListCreated, trainingCardEmployeeRowFromResponse } from '../../utils/trainingCardListSync';
import { isActiveTrainingEmployee } from '../../utils/trainingEmployeeScope';

const SELECT_CLASS = "bg-white block w-full px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40";

const PREVIEW_GROUP_KEYS = [
    ['ready', 'TrainingCardAdd.Preview.Ready'],
    ['existing', 'TrainingCardAdd.Preview.Existing'],
    ['selection_required', 'TrainingCardAdd.Preview.SelectionRequired'],
    ['needs_configuration', 'TrainingCardAdd.Preview.NeedsConfiguration'],
];

function TemplateCandidateRow({
    candidate,
    checked,
    catalogs,
    draft,
    templateRecords,
    t,
    onToggle,
    onCascade,
}) {
    const cascaded = applyCandidateCascade(draft || {}, catalogs);
    const checkable = isCandidateCheckable(candidate, draft, catalogs);
    const configurable = candidate.status === 'selection_required' || candidate.status === 'needs_configuration';
    const departmentName = departmentNameForCandidate(candidate, templateRecords);
    const missing = !candidate.status || candidate.status === 'existing' || candidate.status === 'ready'
        ? []
        : missingConfigurationFieldKeys(candidate, draft, catalogs);
    const missingText = missing.length
        ? t('TrainingCardAdd.Preview.Missing', {
            fields: missing.map((key) => t(`TrainingCardAdd.Preview.Missing.${key}`)).join(', '),
        })
        : '';
    const companyName = catalogLabel(cascaded.companyId || candidate.company_id, catalogs.companies, '');
    const machineText = machineLabels(cascaded.machineIds?.length ? cascaded.machineIds : candidate.machine_ids, catalogs.machines).join(', ');
    const lecturerName = catalogLabel(cascaded.lecturerTypeId || candidate.lecturer_id, catalogs.lecturerTypes, '');
    const trainerName = contactLabel(cascaded.companyContactId || candidate.company_contact_id, catalogs.companyContacts);

    if (!candidate.status) {
        return (
            <div className="flex flex-row border border-black rounded-md p-2 m-1" data-testid={`template-candidate-${candidate.training_id}`} data-status="pending">
                <Checkbox
                    checked={!!checked}
                    onChange={(e) => onToggle(candidate.training_id, e.checked)}
                />
                <span className="ml-2">{[departmentName, candidate.name].filter(Boolean).join(' - ')}</span>
            </div>
        );
    }

    return (
        <div className="flex flex-col border border-black rounded-md p-2 m-1 gap-2" data-testid={`template-candidate-${candidate.training_id}`} data-status={candidate.status}>
            <div className="flex flex-row items-start">
                <Checkbox
                    disabled={!checkable}
                    checked={!!checked}
                    onChange={(e) => onToggle(candidate.training_id, e.checked)}
                />
                <div className="ml-2 flex flex-col">
                    <span className="font-semibold">{candidate.name}</span>
                    <span className="text-sm text-gray-600">
                        {t('TrainingCardAdd.Preview.Department')}: {departmentName || '—'}
                    </span>
                </div>
            </div>
            {configurable ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-6">
                    <div>
                        <label className="block text-sm font-semibold text-gray-800">{t('TrainingCardAdd.Preview.Company')}</label>
                        <select
                            value={cascaded.companyId || ''}
                            onChange={(e) => onCascade(candidate.training_id, 'companyId', e.target.value)}
                            className={SELECT_CLASS}
                        >
                            <option value="">{t('TrainingCardAdd.Preview.Company')}</option>
                            {(catalogs.companies || []).map((company) => (
                                <option key={company.id} value={company.id}>{company.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-800">{t('TrainingCardAdd.Preview.Machines')}</label>
                        <MultiSelect
                            value={(cascaded.availableMachines || []).filter((machine) =>
                                (cascaded.machineIds || []).some((id) => String(id) === String(machine.id))
                            )}
                            onChange={(e) => onCascade(candidate.training_id, 'machineIds', e.value)}
                            options={cascaded.availableMachines || []}
                            optionLabel="name"
                            dataKey="id"
                            disabled={!cascaded.companyId}
                            placeholder={t('TrainingCardAdd.Preview.Machines')}
                            className="w-full px-4 mt-2 text-grey-700 bg-white border rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-800">{t('TrainingCardAdd.Preview.Lecturer')}</label>
                        <select
                            value={cascaded.lecturerTypeId || ''}
                            onChange={(e) => onCascade(candidate.training_id, 'lecturerTypeId', e.target.value)}
                            disabled={!cascaded.companyId}
                            className={SELECT_CLASS}
                        >
                            <option value="">{t('TrainingCardAdd.Preview.Lecturer')}</option>
                            {(cascaded.availableLecturerTypes || []).map((lecturer) => (
                                <option key={lecturer.id} value={lecturer.id}>{lecturer.name || `#${lecturer.id}`}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-800">{t('TrainingCardAdd.Preview.Contact')}</label>
                        <select
                            value={cascaded.companyContactId || ''}
                            onChange={(e) => onCascade(candidate.training_id, 'companyContactId', e.target.value)}
                            disabled={!cascaded.lecturerTypeId}
                            className={SELECT_CLASS}
                        >
                            <option value="">{t('TrainingCardAdd.Preview.Contact')}</option>
                            {(cascaded.availableContacts || []).map((contact) => (
                                <option key={contact.id} value={contact.id}>{contact?.employee?.name || contact?.name || `#${contact.id}`}</option>
                            ))}
                        </select>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 ml-6 text-sm text-gray-700">
                    <span>{t('TrainingCardAdd.Preview.Company')}: {companyName || '—'}</span>
                    <span>{t('TrainingCardAdd.Preview.Machines')}: {machineText || '—'}</span>
                    <span>{t('TrainingCardAdd.Preview.Lecturer')}: {lecturerName || '—'}</span>
                    <span>{t('TrainingCardAdd.Preview.Contact')}: {trainerName || '—'}</span>
                </div>
            )}
            {missingText ? <div className="ml-6 text-sm text-amber-800">{missingText}</div> : null}
        </div>
    );
}

export default function TrainingCardAdd({employee=null}) {

    const [state,dispatch] = useContext(DatabaseContext);
    const {user} = useContext(AuthContext);
    const {toast,openFavoriteDialog,setFastView} = useContext(UtilContext);
    const { t } = useContext(LocaleContext);
    const { trainingCompanies } = useTrainingCompanies();
    const [closeTab,setCloseTab] = useState(false);
    const submitRef = useRef(null);
    const fileListRef = useRef(null);
    const previewGenRef = useRef(0);
    const applyingRef = useRef(false);
    const previewSessionRef = useRef(null);
    const catalogsRef = useRef({ companies: [], machines: [], lecturerTypes: [], companyContacts: [] });

    const ref = useRef(null);
    const [formType,setFormType] = useState("add");

    const [trainingsMarked, setTrainingsMarked] = useState({});
    const [markUnmark, setMarkUnmarked] = useState(true);

    const [formEmployee,setFormEmployee] = useState(employee);
    const [trainingDepartment,setTrainingDepartment] = useState([]);
    const [trainings,setTrainings] = useState([]);

    const [insertCopyIcon,setInsertCopyIcon] = useState((user.permissions?.includes("add training"))?false:true);

    const [fileList, setFileList] = useState([]);
    const [localTemplates, setLocalTemplates] = useState([]);
    const [localCards, setLocalCards] = useState([]);
    const [loadingCardData, setLoadingCardData] = useState(true);
    const [companyContactsList, setCompanyContactsList] = useState([]);
    const [lecturersFallback, setLecturersFallback] = useState([]);
    const [previewSession, setPreviewSession] = useState(() => nextTemplatePreviewSession(null, { type: 'invalidate', generation: 0, shouldFetch: false }));
    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState(null);

    const catalogs = useMemo(() => ({
        companies: trainingCompanies || [],
        machines: state.machines || [],
        lecturerTypes: (state.lecturers || []).length ? (state.lecturers || []) : (lecturersFallback || []),
        companyContacts: companyContactsList || [],
    }), [trainingCompanies, state.machines, state.lecturers, lecturersFallback, companyContactsList]);

    previewSessionRef.current = previewSession;
    catalogsRef.current = catalogs;

    useEffect(() => {
        setLoadingCardData(true);
        const hasLecturers = (state.lecturers || []).length > 0;
        Promise.all([
            getRequest("/api/training_templates").then((r) => r?.data ?? []).catch(() => []),
            getRequest("/api/training_cards").then((r) => r?.data ?? []).catch(() => []),
            getRequest("/api/company_contacts?minimal=1&module=training").then((r) => {
                const list = r?.data ?? r ?? [];
                return Array.isArray(list) ? list : [];
            }).catch(() => []),
            hasLecturers ? Promise.resolve(null) : getRequest("/api/lecturers").then((r) => (r?.data ?? r) || []).catch(() => []),
        ]).then(([templates, cards, contacts, lecturers]) => {
            setLocalTemplates(Array.isArray(templates) ? templates : []);
            setLocalCards(Array.isArray(cards) ? cards : []);
            setCompanyContactsList(Array.isArray(contacts) ? contacts : []);
            if (!hasLecturers && Array.isArray(lecturers)) setLecturersFallback(lecturers);
        }).finally(() => setLoadingCardData(false));
    }, [state.lecturers]);

    function addNewTraining(){
        dispatch({type:'create_new_tab',name: t('TrainingCardAdd.Text.NOVA_KARTA_ZAMESTNANCA'), translationKey: 'TrainingCardAdd.Text.NOVA_KARTA_ZAMESTNANCA', legacyLabel: t('TrainingCardAdd.Text.NOVA_KARTA_ZAMESTNANCA'),page:'TrainingCardAdd',props:{training: null,type:'add'},mask:'trng003'})
    }
    function copyTraining(){
        dispatch({type:'create_new_tab',name: t('TrainingCardAdd.Text.NOVA_KARTA_ZAMESTNANCA'), translationKey: 'TrainingCardAdd.Text.NOVA_KARTA_ZAMESTNANCA', legacyLabel: t('TrainingCardAdd.Text.NOVA_KARTA_ZAMESTNANCA'),page:'TrainingCardAdd',props:{training: formik.values,type:'add'},mask:'trng003'})
    }

    function changeTemplate(template_id){
        setTrainingsMarked({});
        formik.setFieldValue("training_template_id",template_id);
    }

    function copyFromEmployee(value){
        const trainingMarked = {};
        if(value){
            const card = localCards.find((c) => String(c.id) === String(employee?.id));
            (card?.training_card ?? []).forEach((item) => {
                trainingMarked[item.training_id]=true;
            });
        }
        setTrainingsMarked(trainingMarked);
        setPreviewSession(nextTemplatePreviewSession(null, { type: 'invalidate', generation: previewGenRef.current, shouldFetch: false }));
        formik.setFieldValue("copy_records",value);
    }

    function changeMarkings(value){
        const trainingMarked = {...trainingsMarked};
        for(let val in trainingsMarked){
            trainingMarked[val]=value;
        };
        setTrainingsMarked(trainingMarked);
    }

    function setMask(item,value){
        const trainingMarked = {...trainingsMarked};
        trainingMarked[item] = value;
        setTrainingsMarked(trainingMarked);
    }

    const validate = values => {
        const errors = {};
        /*if (!values.type) {
            errors.type = t('Common.RequiredField');
        }*/
        return errors;
    };

    const formik = useFormik({
        initialValues: formEmployee==null?{
            deparment_id: '',initial_date: '',employee_id:'',training_template_id:'',trainings:[],copy_records:false}
            :formEmployee,
        enableReinitialize:true,
        validate,
        onSubmit: values => {
            const isTemplateApply = employee == null && values.training_template_id;
            if (isTemplateApply) {
                const started = beginTemplateApply(applyingRef.current);
                if (!started.started) return;
                applyingRef.current = true;
                setApplying(true);
                setApplyError(null);
                const session = previewSessionRef.current;
                const currentCatalogs = catalogsRef.current;
                if (!values.initial_date) {
                    applyingRef.current = false;
                    setApplying(false);
                    setApplyError({ kind: 'validation' });
                    return;
                }
                if (!session?.preview || !previewMatchesForm(session.preview, values)) {
                    applyingRef.current = false;
                    setApplying(false);
                    setApplyError({ kind: 'validation' });
                    return;
                }
                const payload = buildTemplateApplyPayload({
                    employeeId: values.employee_id,
                    trainingTemplateId: values.training_template_id,
                    initialDate: values.initial_date,
                    selectedIds: session.selectedIds,
                    preview: session.preview,
                    drafts: session.drafts,
                    catalogs: currentCatalogs,
                });
                postRequest(TRAINING_TEMPLATE_APPLY_PATH, payload)
                    .then((response) => {
                        const parsed = parseTemplateApplyResponse(response);
                        if (!parsed.ok) {
                            setApplyError(parsed.error);
                            return;
                        }
                        const counts = templateApplyResultCounts(parsed.applied);
                        setFormType("edit");
                        getRequest("/api/training_cards").then((r) => setLocalCards(r?.data ?? [])).catch(() => {});
                        emitTrainingCardListCreated(trainingCardEmployeeRowFromResponse(response));
                        toast?.current?.show({
                            severity: 'info',
                            summary: t('TrainingCardAdd.Apply.Success', { created: counts.created, skipped: counts.skipped_existing }),
                            life: 4000,
                            detail: t('TrainingCardAdd.Apply.Success', { created: counts.created, skipped: counts.skipped_existing }),
                        });
                        if(closeTab) dispatch({type:'close_tab',id:state.selectedTab});
                    })
                    .catch(() => {
                        setApplyError({ kind: 'network' });
                    })
                    .finally(() => {
                        applyingRef.current = false;
                        setApplying(false);
                    });
                return;
            }
            let formData = new FormData();
            let trainings = Object.keys(trainingsMarked)
            .filter(function(k){return trainingsMarked[k]})
            for (var i = 0; i < trainings.length; i++) {
                formData.append('trainings[]', trainings[i]);
            }
            for (let value in values) {
                if(value!="trainings")formData.append(value, values[value] != null ? values[value] : "");
            }
           if(formType=="add"){
                postRequest(TRAINING_CARD_LEGACY_PATH, formData,true)
                .then((response) => {
                    setFormType("edit");
                    getRequest("/api/training_cards").then((r) => setLocalCards(r?.data ?? [])).catch(() => {});
                    emitTrainingCardListCreated(trainingCardEmployeeRowFromResponse(response));
                    toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordSavedDetail') });
                    if(closeTab) dispatch({type:'close_tab',id:state.selectedTab});
                }).catch((reason) => {
                    if(!navigator.online){
                        alert(t('ServiceTask.OfflineQueuedAlert'));
                    }
                    else{
                        alert(t('Common.Toast.ActionFailedDetail'));
                    }
                })
           }
        },
    });

    useEffect(() => {
        if (employee != null) return;
        const employeeId = formik.values.employee_id;
        const templateId = formik.values.training_template_id;
        const shouldFetch = !!(employeeId && templateId);
        const generation = previewGenRef.current + 1;
        previewGenRef.current = generation;
        setPreviewSession((prev) => nextTemplatePreviewSession(prev, {
            type: 'invalidate',
            generation,
            shouldFetch,
        }));
        setApplyError(null);
        if (!shouldFetch) return undefined;
        const qs = new URLSearchParams({
            employee_id: String(employeeId),
            training_template_id: String(templateId),
        });
        getRequest(`${TRAINING_TEMPLATE_PREVIEW_PATH}?${qs.toString()}`)
            .then((response) => {
                setPreviewSession((prev) => nextTemplatePreviewSession(prev, {
                    type: 'response',
                    generation,
                    response,
                }));
            })
            .catch(() => {
                setPreviewSession((prev) => nextTemplatePreviewSession(prev, {
                    type: 'rejected',
                    generation,
                }));
            });
        return undefined;
    }, [employee, formik.values.employee_id, formik.values.training_template_id]);

    const selectedTemplate = (localTemplates || []).find((template) => template.id == formik.values.training_template_id);
    const templateRecords = selectedTemplate?.template_records ?? [];
    const templateRows = uniqueActiveTemplateRecords(selectedTemplate);
    const previewForOverlay = formik.values.employee_id
        && previewSession.preview
        && String(previewSession.preview.training_template_id) === String(formik.values.training_template_id)
        && String(previewSession.preview.employee_id) === String(formik.values.employee_id)
        ? previewSession.preview
        : null;
    const displayRows = overlayTemplateRowsWithPreview(templateRows, previewForOverlay);
    const groupedCandidates = groupTemplateCandidates(displayRows.filter((row) => row.status));
    const hasPreviewStatuses = displayRows.some((row) => row.status);
    const previewErrorText = previewSession.error
        ? resolveTemplatePreviewErrorMessage(previewSession.error, t)
        : '';
    const applyErrorText = applyError
        ? resolveTemplateApplyErrorMessage(applyError, t)
        : '';
    const isTemplateFlow = employee == null && !!formik.values.training_template_id;
    const saveDisabled = insertCopyIcon || applying || (isTemplateFlow && !formik.values.employee_id);
    const showTemplatePreview = employee == null
        && formik.values.training_template_id
        && formik.values.training_template_id !== ''
        && formik.values.training_template_id !== null;

    function onToggleCandidate(trainingId, checked) {
        setPreviewSession((prev) => nextTemplatePreviewSession(prev, {
            type: 'toggle',
            generation: prev.generation,
            trainingId,
            checked,
            catalogs,
        }));
    }

    function onCandidateCascade(trainingId, field, value) {
        setPreviewSession((prev) => nextTemplatePreviewSession(prev, {
            type: 'cascade',
            generation: prev.generation,
            trainingId,
            field,
            value,
            catalogs,
        }));
    }

    return (
        <>
            <div>
            <div className='w-full h-10 flex flex-row bg-[#333]'>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={saveDisabled} type='submit' onClick={() => {setCloseTab(true);submitRef.current.click();}}><SaveAndCloseIcon/></button></div>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={saveDisabled} type='submit' onClick={() => {submitRef.current.click();}}><SaveIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => addNewTraining()}><AddIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => copyTraining()}><CopyIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" onClick={() => openFavoriteDialog(t('Common.NewTrainingCompany'),"TrainingRecordAdd")}><FavoriteIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button onClick={() => setFastView("TrainingRecordAdd")}><PriceIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><VerticalLineIcon/></div>
        </div>
        {loadingCardData && (
          <div className="flex flex-col items-center justify-center py-8 bg-gray-100 gap-4">
            <InfinitySpin width="120" color="#333" />
            <span className="text-gray-700">{t('Common.LoadingEllipsis')}</span>
          </div>
        )}
        <form onSubmit={formik.handleSubmit}>
        <div className="relative flex flex-col overflow-x min-h-[calc(100vh-177px)] bg-gray-100">
          <div className='xl:grid grid-cols-2 gap-1 mb-2 '>
            <div className="w-auto p-6 m-4 bg-white rounded-md shadow-md">
                <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('TrainingCardAdd.Text.NOVA_KARTA_ZAMESTNANCA')}</h1>
                <div>
                        <label
                            htmlFor="employee_id"
                            className="block text-md font-semibold text-gray-800"
                        >
                            Meno zamestnanca
                        </label>
                        <select
                        required
                        id="employee_id"
                        name='employee_id'
                        onChange={formik.handleChange} value={formik.values.employee_id}
                        className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40">
                          <option value="">{t('Common.SelectEmployee')}</option>
                          {(state.employees || [])
                            .filter(isActiveTrainingEmployee)
                            .filter(a => !localCards.map((b) => b.id).includes(a.id))
                            .map((employee) => (
                            <option key={employee.id} value={employee.id}>{employee.name}</option>
                          ))}
                        </select>
                        </div>
                        {(employee==null) && <div>
                        <label
                            htmlFor="training_template_id"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Ui.R67.Sablona_pre_oddelenie')}</label>
                        <select
                        id="training_template_id"
                        name="training_template_id"
                        onChange={(e) => changeTemplate(e.target.value)} value={formik.values.training_template_id}
                        disabled={loadingCardData}
                        className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40">
                          <option value="">{loadingCardData ? t('Common.Loading') : t('TrainingCardAdd.Text.Bez_sablony')}</option>
                          {localTemplates.map((department) => (
                            <option key={department.id} value={department.id}>{department.name}</option>
                            ))}
                        </select>
                        </div>}
                        {(employee!=null) && <div className="flex align-items-center mt-2">
                            <Checkbox inputId="copy_records" name="copy_records" value="copy_records" onChange={(e) => copyFromEmployee(e.checked)} checked={formik.values.copy_records} />
                            <label htmlFor="copy_records" className="ml-2">{t('Ui.R67.Kopirovat_aj_skolenia_zamestnanca')}</label>
                        </div>}
                      {((formik.values.training_template_id && formik.values.training_template_id!="") || formik.values?.copy_records) && <div>
                        <label
                            htmlFor="initial_date"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Ui.R67.Datum_prvotneho_skolenia')}</label>
                        <input
                            id="initial_date"
                            name='initial_date'
                            type="date" required={employee != null}
                            onChange={formik.handleChange} value={formik.values.initial_date}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                     </div>}
            </div>
            {showTemplatePreview &&  <div data-testid="template-preview-panel" className="w-auto p-6 m-4 bg-white rounded-md shadow-md">
                <div className='flex justify-between'>
                    <h1 className="text-3xl font-semibold text-left text-zinc-900">
                   {selectedTemplate?.name}
                </h1>
                <button type="button" onClick={() => {
                    setPreviewSession((prev) => nextTemplatePreviewSession(prev, {
                        type: 'toggleAll',
                        generation: prev.generation,
                        checked: !markUnmark,
                        catalogs,
                        templateTrainingIds: templateRows.map((row) => row.training_id),
                    }));
                    setMarkUnmarked(!markUnmark);
                }} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-700 rounded-md hover:bg-zinc-500'>{markUnmark ? t('TrainingCardAdd.Preview.UnmarkAll') : t('TrainingCardAdd.Preview.MarkAll')}</button>
                </div>
                {previewSession.loading && (
                    <div className="text-gray-700 py-2">{t('TrainingCardAdd.Preview.Loading')}</div>
                )}
                {!formik.values.employee_id ? (
                    <div data-testid="template-employee-required" className="text-gray-700 py-2">
                        {t('TrainingCardAdd.Preview.EmployeeRequired')}
                    </div>
                ) : null}
                {previewErrorText ? <div className="text-red-700 py-2">{previewErrorText}</div> : null}
                {applyErrorText ? <div className="text-red-700 py-2">{applyErrorText}</div> : null}
                {templateRows.length ? (
                    <div data-testid="template-autocheck-hint" className="text-sm text-gray-600 py-1">
                        {t('TrainingCardAdd.Preview.AutoCheckHint')}
                    </div>
                ) : null}
                <div className="flex flex-col h-[600px] overflow-y-auto">
                    {!templateRows.length ? (
                        <div data-testid="template-empty-state" className="text-gray-700 py-2">
                            {t('TrainingCardAdd.Preview.EmptyTemplate')}
                        </div>
                    ) : hasPreviewStatuses ? (
                    <>
                    {PREVIEW_GROUP_KEYS.map(([status, labelKey]) => {
                        const rows = groupedCandidates[status] || [];
                        if (!rows.length) return null;
                        return (
                            <div key={status} className="mb-3">
                                <h2 className="text-lg font-semibold text-zinc-800 px-1">{t(labelKey)}</h2>
                                {rows.map((candidate) => (
                                    <TemplateCandidateRow
                                        key={candidate.training_id}
                                        candidate={candidate}
                                        checked={previewSession.selectedIds.some((id) => String(id) === String(candidate.training_id))}
                                        catalogs={catalogs}
                                        draft={previewSession.drafts[candidate.training_id] || previewSession.drafts[String(candidate.training_id)]}
                                        templateRecords={templateRecords}
                                        t={t}
                                        onToggle={onToggleCandidate}
                                        onCascade={onCandidateCascade}
                                    />
                                ))}
                            </div>
                        );
                    })}
                    {displayRows.filter((row) => !row.status).map((candidate) => (
                        <TemplateCandidateRow
                            key={candidate.training_id}
                            candidate={candidate}
                            checked={previewSession.selectedIds.some((id) => String(id) === String(candidate.training_id))}
                            catalogs={catalogs}
                            draft={previewSession.drafts[candidate.training_id] || previewSession.drafts[String(candidate.training_id)]}
                            templateRecords={templateRecords}
                            t={t}
                            onToggle={onToggleCandidate}
                            onCascade={onCandidateCascade}
                        />
                    ))}
                    </>
                    ) : (
                        displayRows.map((candidate) => (
                            <TemplateCandidateRow
                                key={candidate.training_id}
                                candidate={candidate}
                                checked={previewSession.selectedIds.some((id) => String(id) === String(candidate.training_id))}
                                catalogs={catalogs}
                                draft={previewSession.drafts[candidate.training_id] || previewSession.drafts[String(candidate.training_id)]}
                                templateRecords={templateRecords}
                                t={t}
                                onToggle={onToggleCandidate}
                                onCascade={onCandidateCascade}
                            />
                        ))
                    )}
                </div>
            </div>}
            {(employee!=null && formik.values?.copy_records) &&  <div data-testid="copy-trainings-panel" className="w-auto p-6 m-4 bg-white rounded-md shadow-md">
                <div className='flex justify-between'>
                    <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('Ui.R67.Kopirovanie_skoleni_zo_zamestnanca')}</h1>
                <button type="button" onClick={() => {changeMarkings(!markUnmark);setMarkUnmarked(!markUnmark);}} className='m-1 px-4 py-2 text-center tracking-wide text-white transition-colors duration-200 transform bg-zinc-700 rounded-md hover:bg-zinc-500'>{markUnmark ? t('Common.DeselectAllUpper') : t('Common.SelectAllUpper')}</button>
                </div>
                <div className="flex flex-col h-[600px] overflow-y-auto">
                    {(localCards.find((c) => c.id == employee?.id)?.training_card ?? []).slice().sort((a,b) => (a.training?.department?.defined_id ?? '') < (b.training?.department?.defined_id ?? '') ? -1 : 1).map(function(item, index){
                        return <div key={index} data-testid={`copy-training-row-${item.training_id}`} className="flex flex-row border border-black rounded-md p-2 m-1">
                            <Checkbox onChange={(e) => setMask(item.training_id,e.checked)} checked={trainingsMarked[item.training_id]}></Checkbox><span className="ml-2">{item.training.department.name + " - " + item.training.name}</span>
                        </div> ;
                    })}
                </div>
            </div>}
            </div>
            </div>
            <button type='submit' ref={submitRef}></button>
             </form>
            </div>
            </>
            );

}
